import { useCallback, useRef } from 'react';

type SoundType = 'select' | 'success' | 'failure' | 'alert' | 'activate' | 'countdown' | 'win' | 'overshoot' | 'tick' | 'whoosh' | 'pop' | 'levelup' | 'closecall' | 'tension' | 'hype' | 'rematch';

export const useSound = () => {
  const audioContextRef = useRef<AudioContext | null>(null);

  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);

  // Create a filter for warmer, more modern sounds
  const createFilter = useCallback((ctx: AudioContext, type: BiquadFilterType, freq: number) => {
    const filter = ctx.createBiquadFilter();
    filter.type = type;
    filter.frequency.value = freq;
    filter.Q.value = 1;
    return filter;
  }, []);

  const playSound = useCallback((type: SoundType, volume: number = 0.3) => {
    try {
      const ctx = getAudioContext();
      const now = ctx.currentTime;
      
      const gainNode = ctx.createGain();
      gainNode.connect(ctx.destination);
      gainNode.gain.value = volume;

      switch (type) {
        case 'select': {
          // Modern UI click - soft pluck with reverb feel
          const osc = ctx.createOscillator();
          const osc2 = ctx.createOscillator();
          osc.type = 'sine';
          osc2.type = 'triangle';
          osc.frequency.setValueAtTime(800, now);
          osc.frequency.exponentialRampToValueAtTime(1200, now + 0.03);
          osc2.frequency.setValueAtTime(1600, now);
          osc2.frequency.exponentialRampToValueAtTime(800, now + 0.05);
          
          const filter = createFilter(ctx, 'lowpass', 3000);
          osc.connect(filter);
          osc2.connect(filter);
          filter.connect(gainNode);
          
          gainNode.gain.setValueAtTime(volume * 0.6, now);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.08);
          osc.start(now);
          osc2.start(now);
          osc.stop(now + 0.08);
          osc2.stop(now + 0.08);
          break;
        }

        case 'success': {
          // Satisfying notification chime - TikTok style
          const notes = [880, 1320, 1760];
          notes.forEach((freq, i) => {
            const osc = ctx.createOscillator();
            osc.type = 'sine';
            osc.frequency.value = freq;
            const oscGain = ctx.createGain();
            oscGain.gain.setValueAtTime(0, now + i * 0.08);
            oscGain.gain.linearRampToValueAtTime(volume * 0.4, now + i * 0.08 + 0.02);
            oscGain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.08 + 0.15);
            osc.connect(oscGain);
            oscGain.connect(gainNode);
            osc.start(now + i * 0.08);
            osc.stop(now + i * 0.08 + 0.2);
          });
          break;
        }

        case 'failure': {
          // Modern "bruh" fail sound - low wobble
          const osc = ctx.createOscillator();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(200, now);
          osc.frequency.setValueAtTime(180, now + 0.1);
          osc.frequency.setValueAtTime(150, now + 0.2);
          osc.frequency.exponentialRampToValueAtTime(80, now + 0.4);
          
          const filter = createFilter(ctx, 'lowpass', 400);
          osc.connect(filter);
          filter.connect(gainNode);
          
          gainNode.gain.setValueAtTime(volume, now);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
          osc.start(now);
          osc.stop(now + 0.5);
          break;
        }

        case 'alert': {
          // Modern notification ping with echo
          const osc = ctx.createOscillator();
          osc.type = 'sine';
          osc.frequency.value = 880;
          
          [0, 0.15, 0.3].forEach((delay, i) => {
            const echoGain = ctx.createGain();
            echoGain.gain.value = volume * (1 - i * 0.3);
            const echoOsc = ctx.createOscillator();
            echoOsc.type = 'sine';
            echoOsc.frequency.value = 880;
            echoOsc.connect(echoGain);
            echoGain.connect(ctx.destination);
            echoOsc.start(now + delay);
            echoOsc.stop(now + delay + 0.1);
            echoGain.gain.exponentialRampToValueAtTime(0.01, now + delay + 0.15);
          });
          break;
        }

        case 'activate': {
          // Power-up activation - swoosh up with shimmer
          const osc = ctx.createOscillator();
          const osc2 = ctx.createOscillator();
          osc.type = 'sine';
          osc2.type = 'triangle';
          osc.frequency.setValueAtTime(300, now);
          osc.frequency.exponentialRampToValueAtTime(1200, now + 0.2);
          osc2.frequency.setValueAtTime(600, now);
          osc2.frequency.exponentialRampToValueAtTime(2400, now + 0.2);
          
          const filter = createFilter(ctx, 'bandpass', 1000);
          filter.Q.value = 2;
          osc.connect(filter);
          osc2.connect(filter);
          filter.connect(gainNode);
          
          gainNode.gain.setValueAtTime(volume * 0.5, now);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.25);
          osc.start(now);
          osc2.start(now);
          osc.stop(now + 0.25);
          osc2.stop(now + 0.25);
          break;
        }

        case 'countdown': {
          // Heartbeat-style thump for countdown
          const osc = ctx.createOscillator();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(80, now);
          osc.frequency.exponentialRampToValueAtTime(40, now + 0.1);
          
          const filter = createFilter(ctx, 'lowpass', 150);
          osc.connect(filter);
          filter.connect(gainNode);
          
          gainNode.gain.setValueAtTime(volume * 1.5, now);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.15);
          osc.start(now);
          osc.stop(now + 0.15);
          break;
        }

        case 'win': {
          // Epic victory fanfare - layered synth celebration
          const chordNotes = [523, 659, 784, 1047, 1319];
          chordNotes.forEach((freq, i) => {
            const osc = ctx.createOscillator();
            const osc2 = ctx.createOscillator();
            osc.type = 'sine';
            osc2.type = 'triangle';
            osc.frequency.value = freq;
            osc2.frequency.value = freq * 1.01; // Slight detune for richness
            
            const oscGain = ctx.createGain();
            oscGain.gain.setValueAtTime(0, now + i * 0.12);
            oscGain.gain.linearRampToValueAtTime(volume * 0.3, now + i * 0.12 + 0.05);
            oscGain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.12 + 0.6);
            
            osc.connect(oscGain);
            osc2.connect(oscGain);
            oscGain.connect(gainNode);
            
            osc.start(now + i * 0.12);
            osc2.start(now + i * 0.12);
            osc.stop(now + i * 0.12 + 0.7);
            osc2.stop(now + i * 0.12 + 0.7);
          });
          
          // Add shimmer sweep
          const sweep = ctx.createOscillator();
          sweep.type = 'sawtooth';
          sweep.frequency.setValueAtTime(2000, now);
          sweep.frequency.exponentialRampToValueAtTime(8000, now + 0.8);
          const sweepFilter = createFilter(ctx, 'bandpass', 4000);
          sweepFilter.Q.value = 5;
          const sweepGain = ctx.createGain();
          sweepGain.gain.setValueAtTime(volume * 0.1, now);
          sweepGain.gain.exponentialRampToValueAtTime(0.01, now + 0.8);
          sweep.connect(sweepFilter);
          sweepFilter.connect(sweepGain);
          sweepGain.connect(ctx.destination);
          sweep.start(now);
          sweep.stop(now + 0.8);
          break;
        }

        case 'overshoot': {
          // Dramatic fail - vinyl scratch + bass drop feel
          const noise = ctx.createOscillator();
          noise.type = 'sawtooth';
          noise.frequency.setValueAtTime(800, now);
          noise.frequency.exponentialRampToValueAtTime(100, now + 0.3);
          
          const bass = ctx.createOscillator();
          bass.type = 'sine';
          bass.frequency.setValueAtTime(100, now + 0.1);
          bass.frequency.exponentialRampToValueAtTime(30, now + 0.5);
          
          const filter = createFilter(ctx, 'lowpass', 500);
          noise.connect(filter);
          bass.connect(gainNode);
          filter.connect(gainNode);
          
          gainNode.gain.setValueAtTime(volume, now);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.6);
          
          noise.start(now);
          bass.start(now + 0.1);
          noise.stop(now + 0.4);
          bass.stop(now + 0.6);
          break;
        }

        case 'tick': {
          // Crisp digital tick
          const osc = ctx.createOscillator();
          osc.type = 'square';
          osc.frequency.value = 2000;
          
          const filter = createFilter(ctx, 'highpass', 1500);
          osc.connect(filter);
          filter.connect(gainNode);
          
          gainNode.gain.setValueAtTime(volume * 0.4, now);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.02);
          osc.start(now);
          osc.stop(now + 0.02);
          break;
        }

        case 'whoosh': {
          // Smooth transition whoosh
          const osc = ctx.createOscillator();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(100, now);
          osc.frequency.exponentialRampToValueAtTime(2000, now + 0.2);
          osc.frequency.exponentialRampToValueAtTime(500, now + 0.3);
          
          const filter = createFilter(ctx, 'bandpass', 800);
          filter.Q.value = 0.5;
          osc.connect(filter);
          filter.connect(gainNode);
          
          gainNode.gain.setValueAtTime(0, now);
          gainNode.gain.linearRampToValueAtTime(volume * 0.5, now + 0.1);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
          osc.start(now);
          osc.stop(now + 0.3);
          break;
        }

        case 'pop': {
          // Bubbly pop - satisfying
          const osc = ctx.createOscillator();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(1000, now);
          osc.frequency.exponentialRampToValueAtTime(300, now + 0.06);
          
          const osc2 = ctx.createOscillator();
          osc2.type = 'triangle';
          osc2.frequency.setValueAtTime(1500, now);
          osc2.frequency.exponentialRampToValueAtTime(400, now + 0.04);
          
          osc.connect(gainNode);
          osc2.connect(gainNode);
          
          gainNode.gain.setValueAtTime(volume * 0.6, now);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.1);
          osc.start(now);
          osc2.start(now);
          osc.stop(now + 0.1);
          osc2.stop(now + 0.1);
          break;
        }

        case 'levelup': {
          // Triumphant level up - sparkly arpeggio
          const notes = [523, 659, 784, 1047, 1319, 1568];
          notes.forEach((freq, i) => {
            const osc = ctx.createOscillator();
            osc.type = 'sine';
            osc.frequency.value = freq;
            const oscGain = ctx.createGain();
            oscGain.gain.setValueAtTime(0, now + i * 0.08);
            oscGain.gain.linearRampToValueAtTime(volume * 0.25, now + i * 0.08 + 0.02);
            oscGain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.08 + 0.4);
            osc.connect(oscGain);
            oscGain.connect(gainNode);
            osc.start(now + i * 0.08);
            osc.stop(now + i * 0.08 + 0.5);
          });
          
          // Shimmer overlay
          const shimmer = ctx.createOscillator();
          shimmer.type = 'sine';
          shimmer.frequency.value = 3000;
          const shimmerGain = ctx.createGain();
          shimmerGain.gain.setValueAtTime(0, now);
          shimmerGain.gain.linearRampToValueAtTime(volume * 0.1, now + 0.3);
          shimmerGain.gain.exponentialRampToValueAtTime(0.01, now + 0.8);
          shimmer.connect(shimmerGain);
          shimmerGain.connect(ctx.destination);
          shimmer.start(now);
          shimmer.stop(now + 0.8);
          break;
        }

        case 'closecall': {
          // Tension building - low pulse with rising pitch
          const osc = ctx.createOscillator();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(60, now);
          osc.frequency.linearRampToValueAtTime(120, now + 0.3);
          
          const osc2 = ctx.createOscillator();
          osc2.type = 'triangle';
          osc2.frequency.setValueAtTime(200, now);
          osc2.frequency.linearRampToValueAtTime(400, now + 0.3);
          
          const filter = createFilter(ctx, 'lowpass', 300);
          osc.connect(filter);
          osc2.connect(filter);
          filter.connect(gainNode);
          
          gainNode.gain.setValueAtTime(volume * 0.8, now);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.4);
          osc.start(now);
          osc2.start(now);
          osc.stop(now + 0.4);
          osc2.stop(now + 0.4);
          break;
        }

        case 'tension': {
          // Suspenseful drone
          const osc = ctx.createOscillator();
          osc.type = 'sawtooth';
          osc.frequency.setValueAtTime(80, now);
          
          const lfo = ctx.createOscillator();
          lfo.type = 'sine';
          lfo.frequency.value = 4;
          const lfoGain = ctx.createGain();
          lfoGain.gain.value = 20;
          lfo.connect(lfoGain);
          lfoGain.connect(osc.frequency);
          
          const filter = createFilter(ctx, 'lowpass', 200);
          osc.connect(filter);
          filter.connect(gainNode);
          
          gainNode.gain.setValueAtTime(volume * 0.4, now);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.6);
          lfo.start(now);
          osc.start(now);
          lfo.stop(now + 0.6);
          osc.stop(now + 0.6);
          break;
        }

        case 'hype': {
          // Hype build-up - rising synth with drop
          const osc = ctx.createOscillator();
          osc.type = 'sawtooth';
          osc.frequency.setValueAtTime(200, now);
          osc.frequency.exponentialRampToValueAtTime(800, now + 0.3);
          
          const filter = createFilter(ctx, 'lowpass', 2000);
          filter.frequency.setValueAtTime(500, now);
          filter.frequency.exponentialRampToValueAtTime(4000, now + 0.3);
          
          osc.connect(filter);
          filter.connect(gainNode);
          
          gainNode.gain.setValueAtTime(volume * 0.3, now);
          gainNode.gain.linearRampToValueAtTime(volume * 0.6, now + 0.25);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.4);
          
          osc.start(now);
          osc.stop(now + 0.4);
          break;
        }

        case 'rematch': {
          // Attention-grabbing notification - double chime with urgency
          const notes = [880, 1320, 880, 1320];
          const timings = [0, 0.12, 0.4, 0.52];
          
          notes.forEach((freq, i) => {
            const osc = ctx.createOscillator();
            const osc2 = ctx.createOscillator();
            osc.type = 'sine';
            osc2.type = 'triangle';
            osc.frequency.value = freq;
            osc2.frequency.value = freq * 2;
            
            const oscGain = ctx.createGain();
            oscGain.gain.setValueAtTime(0, now + timings[i]);
            oscGain.gain.linearRampToValueAtTime(volume * 0.5, now + timings[i] + 0.02);
            oscGain.gain.exponentialRampToValueAtTime(0.01, now + timings[i] + 0.15);
            
            osc.connect(oscGain);
            osc2.connect(oscGain);
            oscGain.connect(gainNode);
            
            osc.start(now + timings[i]);
            osc2.start(now + timings[i]);
            osc.stop(now + timings[i] + 0.2);
            osc2.stop(now + timings[i] + 0.2);
          });
          break;
        }
      }
    } catch (error) {
      // Fail silently if audio is unavailable
      console.debug('Sound playback failed:', error);
    }
  }, [getAudioContext, createFilter]);

  return { playSound };
};
